import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import LoadingPopup from '../../../SharedView/LoadingPopup';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faTimes } from '@fortawesome/free-solid-svg-icons';
import Select from 'react-select';
import { DateTime } from 'luxon';

const reportOptions = [
  { value: 'task_report', label: 'Task Report' },
  // If you want to allow changing type inside task modal, add more options here.
  // { value: 'damages', label: 'Damages' },
  // { value: 'apartment_condition', label: 'Apartment Condition' },
];

function TaskReportModal({ bookingId, task, onClose, onUploaded }) {
  const [comment, setComment] = useState('');
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [fileProgress, setFileProgress] = useState({});

  const [existingReports, setExistingReports] = useState([]);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewReport, setPreviewReport] = useState(null);

  // "Link an existing report" — booking reports not already on this task
  const [showLink, setShowLink] = useState(false);
  const [linkable, setLinkable] = useState([]);
  const [linkingId, setLinkingId] = useState(null);

  // ✅ Edit/approve like BookingReports
  const [editingReportId, setEditingReportId] = useState(null);
  const [editedText, setEditedText] = useState({ type: 'task_report', comment: '', is_approved: false });
  const [isProcessing, setIsProcessing] = useState(false);

  const fileInputRef = useRef(null);

  const taskId = useMemo(() => {
    const v = task?.id != null ? parseInt(task.id, 10) : NaN;
    return Number.isFinite(v) ? v : null;
  }, [task]);

  const isVideoUrl = (url) => /\.(mp4|mov|avi|mkv|m4v|3gp|3g2|webm|qt)$/i.test(String(url || ''));

  const formatType = (type) => {
    const found = reportOptions.find((o) => o.value === type);
    return found ? found.label : String(type || '').replace(/_/g, ' ');
  };

  const fetchTaskReports = async () => {
    if (!taskId) return;
    try {
      const r = await axios.get(`/api/booking-reports/task/${taskId}`);
      setExistingReports(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      console.error('Error fetching task reports:', e);
      setExistingReports([]);
    }
  };

  // Booking reports NOT already linked to this task — candidates to link.
  const fetchLinkable = async () => {
    try {
      const r = await axios.get(`/api/booking-reports/${bookingId}`);
      const all = Array.isArray(r.data) ? r.data : [];
      setLinkable(all.filter((rep) => rep.task_id !== taskId));
    } catch (e) {
      console.error('Error fetching linkable reports:', e);
      setLinkable([]);
    }
  };

  const toggleLink = () => {
    const next = !showLink;
    setShowLink(next);
    if (next) fetchLinkable();
  };

  // Link an already-uploaded report to this task (marks it a task report so
  // approval completes the task; if it's already approved, the task completes now).
  const linkReport = async (reportId) => {
    setLinkingId(reportId);
    try {
      const res = await axios.put(`/api/booking-reports/${reportId}`, { task_id: taskId, type: 'task_report' });
      setFeedbackMessage(res.data?.task_completed
        ? 'Report linked — task marked completed.'
        : 'Report linked to this task.');
      setShowLink(false);
      await fetchTaskReports();
      if (onUploaded) onUploaded();
    } catch (e) {
      console.error('Error linking report:', e);
      setFeedbackMessage(e.response?.data?.error || 'Failed to link the report.');
    } finally {
      setLinkingId(null);
    }
  };

  useEffect(() => {
    fetchTaskReports();

    // silent refresh every 15 sec while modal is open
    const interval = setInterval(() => {
      fetchTaskReports();
    }, 15000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId]);

  const handleFileChange = (e) => {
    const selected = Array.from(e.target.files || []);
    setFiles((prev) => [...prev, ...selected]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFiles = Array.from(e.dataTransfer.files || []);
    setFiles((prev) => [...prev, ...droppedFiles]);
  };

  // Multipart uploader (same style as AddReportModal)
  const uploadFileMultipart = async (file, bookingIdArg) => {
    const PART_SIZE = 8 * 1024 * 1024; // >=5MB except last part
    const MAX_RETRIES = 3;
    const BASE_BACKOFF_MS = 500;
    const safeType = file.type || 'application/octet-stream';

    setFileProgress((prev) => ({ ...prev, [file.name]: 0 }));

    // 1) INIT — v2 (AWS S3 Mumbai) FIRST: same-region-ish uploads for the PH
    // admins instead of Frankfurt. ANY v2 init failure (flag off, old server,
    // creds) silently falls back to the untouched v1/DO rail.
    let rail = 'v1';
    let uploadId, key, fileUrl;
    try {
      const { data: v2init } = await axios.post('/api/uploads/v2/multipart-init', {
        kind: 'report-media',
        booking_id: bookingIdArg,
        file_name: file.name,
        content_type: safeType,
      });
      ({ uploadId, key } = v2init);
      fileUrl = v2init.file_url;
      rail = 'v2';
    } catch (v2Err) {
      const { data: init } = await axios.post('/api/booking-reports/multipart-init', {
        booking_id: bookingIdArg,
        fileName: file.name,
        fileType: safeType,
      });
      ({ uploadId, key, fileUrl } = init);
    }
    const PRESIGN_URL = rail === 'v2' ? '/api/uploads/v2/multipart-presign' : '/api/booking-reports/multipart-presign';
    // v2 completion needs each part's ETag (the buckets expose it via CORS).
    const etagByPart = {};

    const totalBytes = file.size;

    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

    try {
      // 2) Upload parts with a small pool — a single stream leaves most of a
    // high-latency link idle (PLAN_upload_speed P0). Per-part progress is
    // summed across in-flight parts so the bar stays truthful.
    const PART_CONCURRENCY = 4;
    const parts = [];
    for (let start = 0, pn = 1; start < file.size; start += PART_SIZE, pn++) {
        parts.push({ partNumber: pn, start, end: Math.min(start + PART_SIZE, file.size) });
    }
    const loadedByPart = {};
    const reportProgress = () => {
        const current = Object.values(loadedByPart).reduce((a, b) => a + b, 0);
        const percent = Math.min(100, Math.round((current / totalBytes) * 100));
        setFileProgress((prev) => ({ ...prev, [file.name]: percent }));
    };
    let nextPartIdx = 0;
    let poolErr = null;
    const partWorker = async () => {
        for (;;) {
            if (poolErr) return;
            const idx = nextPartIdx++;
            if (idx >= parts.length) return;
            const { partNumber, start, end } = parts[idx];
            const blobPart = file.slice(start, end);
            const partSize = blobPart.size;
            let attempt = 0;
            let uploaded = false;
            while (attempt < MAX_RETRIES && !uploaded) {
                try {
                    const { data: presign } = await axios.post(PRESIGN_URL, {
                        key,
                        uploadId,
                        partNumber,
                    });
                    const putRes = await axios.put(presign.url, blobPart, {
                        withCredentials: false,
                        onUploadProgress: (evt) => {
                            loadedByPart[partNumber] = Math.min(evt.loaded || 0, partSize);
                            reportProgress();
                        },
                    });
                    if (rail === 'v2') {
                      const etag = putRes.headers?.etag || putRes.headers?.ETag;
                      if (!etag) throw new Error(`Part ${partNumber} uploaded but no ETag was exposed`);
                      etagByPart[partNumber] = etag;
                    }
                    uploaded = true;
                    loadedByPart[partNumber] = partSize;
                    reportProgress();
                } catch (e) {
                    attempt += 1;
                    loadedByPart[partNumber] = 0;
                    if (attempt >= MAX_RETRIES) {
                        poolErr = e;
                        throw e; // bubble out to abort
                    }
                    await sleep(BASE_BACKOFF_MS * Math.pow(2, attempt - 1));
                }
            }
        }
    };
    {
        const workerCount = Math.max(1, Math.min(PART_CONCURRENCY, parts.length));
        const results = await Promise.allSettled(Array.from({ length: workerCount }, partWorker));
        const rejected = results.find((r) => r.status === 'rejected');
        if (rejected) throw rejected.reason;
    }

      // 3) COMPLETE — v2 supplies the ETag list; v1 lets the server list parts.
      let completedKey, completedUrl;
      if (rail === 'v2') {
        const partsList = Object.keys(etagByPart).map(Number).sort((a, b) => a - b)
          .map((pn) => ({ PartNumber: pn, ETag: etagByPart[pn] }));
        const { data: complete } = await axios.post('/api/uploads/v2/multipart-complete', { key, uploadId, parts: partsList });
        completedKey = complete.key || key;
        completedUrl = complete.file_url || fileUrl;
      } else {
        const { data: complete } = await axios.post('/api/booking-reports/multipart-complete', { key, uploadId });
        completedKey = complete.fileKey || key;
        completedUrl = complete.fileUrl || fileUrl;
      }

      setFileProgress((prev) => ({ ...prev, [file.name]: 100 }));

      return {
        fileKey: completedKey,
        fileUrl: completedUrl,
        mimeType: safeType,
        fileType: safeType,
      };
    } catch (err) {
      try {
        await axios.post('/api/booking-reports/multipart-abort', { key, uploadId });
      } catch (_) { }
      throw err;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!taskId) {
      setFeedbackMessage('Invalid task.');
      return;
    }
    if (!bookingId) {
      setFeedbackMessage('Missing booking id.');
      return;
    }
    if (files.length === 0) {
      setFeedbackMessage('Please attach at least one file.');
      return;
    }

    try {
      setLoading(true);
      setFeedbackMessage('Uploading...');

      // 1) upload all files
      const uploaded = [];
      for (const file of files) {
        setFileProgress((prev) => ({ ...prev, [file.name]: 0 }));
        const meta = await uploadFileMultipart(file, bookingId);
        uploaded.push({
          fileKey: meta.fileKey,
          fileUrl: meta.fileUrl,
          mimeType: meta.mimeType || file.type || null,
          fileType: meta.fileType || file.type || null,
        });
      }

      // 2) save all to DB with task_id
      await axios.post('/api/booking-reports/upload-report-multipart', {
        booking_id: bookingId,
        type: 'task_report',
        comment: comment || null,
        task_id: taskId,
        files: uploaded,
      });

      setFiles([]);
      setComment('');
      setFileProgress({});
      setFeedbackMessage('Report added successfully!');

      await fetchTaskReports();
      await onUploaded?.();
    } catch (error) {
      console.error('Error uploading task report (multipart):', error);
      setFeedbackMessage(error.response?.data?.error || 'Failed to add report.');
    } finally {
      setLoading(false);
    }
  };

  const openPreview = (report) => {
    if (!report) return;
    if (!report.is_compressed) return;
    setPreviewReport(report);
    setIsPreviewOpen(true);
  };

  const closePreview = () => {
    setIsPreviewOpen(false);
    setPreviewReport(null);
  };

  const closeAndReset = () => {
    onClose?.();
  };

  // ✅ Edit/approve handlers (same endpoints as BookingReports)
  const handleEdit = (report) => {
    if (!report?.id) return;

    if (editingReportId === report.id) {
      setEditingReportId(null);
      setEditedText({ type: 'task_report', comment: '', is_approved: false });
      return;
    }

    setEditingReportId(report.id);
    setEditedText({
      type: report.type || 'task_report',
      comment: report.comment || '',
      is_approved: !!report.is_approved,
    });
  };

  const handleSave = async (reportId) => {
    setIsProcessing(true);
    setFeedbackMessage('Saving report...');
    try {
      await axios.put(`/api/booking-reports/${reportId}`, editedText);

      setExistingReports((prev) =>
        prev.map((r) => (r.id === reportId ? { ...r, ...editedText } : r))
      );

      setEditingReportId(null);
      setFeedbackMessage('Report updated successfully.');
      await onUploaded?.();
    } catch (error) {
      console.error('Error updating report:', error);
      setFeedbackMessage(error.response?.data?.error || 'Failed to update report.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDelete = async (reportId) => {
    if (!window.confirm('Are you sure you want to delete this report? This action cannot be undone.')) return;

    setIsProcessing(true);
    setFeedbackMessage('Deleting report...');
    try {
      await axios.delete(`/api/booking-reports/${reportId}`);
      setExistingReports((prev) => prev.filter((r) => r.id !== reportId));
      setFeedbackMessage('Report deleted successfully.');
      await onUploaded?.();
    } catch (error) {
      console.error('Error deleting report:', error);
      setFeedbackMessage(error.response?.data?.error || 'Failed to delete report.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleQuickApprove = async (reportId) => {
    setIsProcessing(true);
    setFeedbackMessage('Approving report...');
    try {
      await axios.post(`/api/booking-reports/${reportId}/approve`);
      setExistingReports((prev) =>
        prev.map((r) => (r.id === reportId ? { ...r, is_approved: true } : r))
      );
      setFeedbackMessage('Report approved.');
      await onUploaded?.();
    } catch (error) {
      console.error('Approval failed:', error);
      setFeedbackMessage(error.response?.data?.error || 'Failed to approve report.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="popup-overlay" onMouseDown={closeAndReset}>
      <div className="add-report-popup" onMouseDown={(e) => e.stopPropagation()}>
        <button type="button" className="modal-x" onClick={closeAndReset}>&times;</button>

        <h2>Add Task Report</h2>

        <div style={{ fontSize: 15, color: '#1b1b1cff' }}>
          <div>
            <strong>Task:</strong> {task?.instruction || '—'}
          </div>
        </div>

        {/* Existing reports list (with edit/approve like BookingReports) */}
        <div style={{ marginBottom: 12, marginTop: 12 }}>
          <div className="booking-reports-grid">
            {existingReports.map((report) => {
              const isEditing = editingReportId === report.id;

              return (
                <div key={report.id} className="booking-report-card">
                  <div
                    className="booking-report-thumbnail"
                    onClick={() => report.is_compressed && openPreview(report)}
                    title={report.is_compressed ? 'Click to view' : 'Processing...'}
                    style={{ cursor: report.is_compressed ? 'pointer' : 'default' }}
                  >
                    {!report.is_compressed ? (
                      <div className="report-processing-placeholder">
                        <div className="spinner" />
                        <p>Processing...</p>
                      </div>
                    ) : isVideoUrl(report.file_url) ? (
                      <div className="report-thumbnail-wrapper">
                        <img
                          src={report.thumbnail_url || report.file_url}
                          alt="Video Thumbnail"
                          className="booking-report-image-preview"
                        />
                        <div className="play-button-overlay">&#9658;</div>
                      </div>
                    ) : (
                      <img
                        src={report.file_url}
                        alt="Report Attachment"
                        className="booking-report-image-preview"
                      />
                    )}
                  </div>

                  <div className="booking-report-header">
                    {isEditing ? (
                      <Select
                        className="report-edit-select"
                        options={reportOptions}
                        value={reportOptions.find((o) => o.value === editedText.type)}
                        onChange={(selected) =>
                          setEditedText((p) => ({ ...p, type: selected?.value || 'task_report' }))
                        }
                      />
                    ) : (
                      <p className="booking-report-type">{formatType(report.type)}</p>
                    )}

                    {isEditing ? (
                      <FontAwesomeIcon
                        icon={faTimes}
                        className="report-cancel-icon"
                        onClick={() => handleEdit(report)}
                        title="Cancel edit"
                      />
                    ) : (
                      report.is_compressed && (
                        <FontAwesomeIcon
                          icon={faPen}
                          className="report-edit-icon"
                          onClick={() => handleEdit(report)}
                          title="Edit / Approve"
                        />
                      )
                    )}
                  </div>

                  {isEditing ? (
                    <div className="report-edit-mode">
                      <input
                        type="text"
                        className="report-edit-input"
                        placeholder="Comment"
                        value={editedText.comment}
                        onChange={(e) => setEditedText((p) => ({ ...p, comment: e.target.value }))}
                      />

                      <label className="report-edit-checkbox">
                        <input
                          type="checkbox"
                          checked={!!editedText.is_approved}
                          onChange={(e) => setEditedText((p) => ({ ...p, is_approved: e.target.checked }))}
                        />
                        Approved
                      </label>

                      <div className="report-edit-buttons">
                        <button
                          type="button"
                          className="report-delete-button"
                          onClick={() => handleDelete(report.id)}
                        >
                          Delete
                        </button>
                        <button
                          type="button"
                          className="report-save-button"
                          onClick={() => handleSave(report.id)}
                        >
                          Save
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <p className="booking-report-date">
                        <strong>
                          {report.uploaded_at
                            ? DateTime.fromISO(report.uploaded_at, { zone: 'utc' })
                              .setZone('Asia/Dubai')
                              .toFormat('dd LLLL yyyy, HH:mm')
                            : ''}
                        </strong>
                      </p>

                      <p className="booking-report-comment">
                        <strong>Comment:</strong> {report.comment || 'No comment'}
                      </p>

                      <p className="booking-report-comment">
                        <strong>Approved:</strong>{' '}
                        <span className={report.is_approved ? 'approved-text' : 'not-approved-text'}>
                          {report.is_approved ? ' Yes' : ' No'}
                        </span>
                        {!report.is_approved && report.is_compressed && (
                          <button
                            type="button"
                            className="report-approve-button"
                            onClick={() => handleQuickApprove(report.id)}
                            style={{ marginLeft: 10 }}
                          >
                            Approve
                          </button>
                        )}
                      </p>
                    </>
                  )}
                </div>
              );
            })}
          </div>

          {existingReports.length === 0 && (
            <div style={{ fontSize: 13, color: '#6b7280', marginTop: 8 }}>
              No reports uploaded for this task yet.
            </div>
          )}
        </div>

        {/* Link an existing report to this task (instead of re-uploading) */}
        <div className="trm-link">
          <button type="button" className="trm-link-toggle" onClick={toggleLink}>
            {showLink ? 'Hide existing reports' : 'Link an existing report to this task'}
          </button>
          {showLink && (
            linkable.length === 0 ? (
              <div className="trm-link-empty">No other reports on this booking to link.</div>
            ) : (
              <div className="trm-link-grid">
                {linkable.map((rep) => {
                  const isVid = isVideoUrl(rep.file_url);
                  const thumb = rep.thumbnail_url || (!isVid ? rep.file_url : null);
                  return (
                    <div className="trm-link-item" key={rep.id}>
                      {thumb ? (
                        <img
                          src={thumb}
                          alt="report"
                          className={`trm-link-thumb${rep.is_compressed ? ' trm-link-thumb--clickable' : ''}`}
                          title={rep.is_compressed ? 'Click to view' : undefined}
                          onClick={() => rep.is_compressed && openPreview(rep)}
                        />
                      ) : (
                        <div
                          className={`trm-link-thumb trm-link-thumb--vid${rep.is_compressed ? ' trm-link-thumb--clickable' : ''}`}
                          title={rep.is_compressed ? 'Click to view' : undefined}
                          onClick={() => rep.is_compressed && openPreview(rep)}
                        >
                          ▶
                        </div>
                      )}
                      <div className="trm-link-meta">
                        <div className="trm-link-type">{formatType(rep.type)}</div>
                        <div className="trm-link-sub">
                          {rep.task_id ? 'Linked to another task' : 'Not linked'}
                          {rep.is_approved ? ' · Approved' : ''}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="trm-link-btn"
                        onClick={() => linkReport(rep.id)}
                        disabled={linkingId === rep.id}
                      >
                        {linkingId === rep.id ? 'Linking…' : 'Use for this task'}
                      </button>
                    </div>
                  );
                })}
              </div>
            )
          )}
        </div>

        {/* Upload form */}
        <form onSubmit={handleSubmit} className="booking-report-form">
          <div className="form-group">
            <label>Comment:</label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Enter comment..."
              className="textarea-field"
              disabled={loading}
            />
          </div>

          <div
            className="drop-zone"
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <p>Drag & drop files here or click to browse</p>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,video/*"
              onChange={handleFileChange}
              className="file-input"
              style={{ display: 'none' }}
              disabled={loading}
            />
          </div>

          {files.length > 0 && (
            <ul className="file-list">
              {files.map((f, i) => (
                <li key={`${f.name}-${i}`}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>{f.name}</span>
                    {typeof fileProgress[f.name] === 'number' && (
                      <span style={{ marginLeft: 8, minWidth: 48, textAlign: 'right' }}>
                        {fileProgress[f.name]}%
                      </span>
                    )}
                  </div>

                  {typeof fileProgress[f.name] === 'number' && (
                    <div
                      style={{
                        height: 6,
                        background: '#eee',
                        borderRadius: 4,
                        overflow: 'hidden',
                        marginTop: 6,
                      }}
                    >
                      <div
                        style={{
                          width: `${fileProgress[f.name]}%`,
                          height: '100%',
                          background: '#4caf50',
                          transition: 'width 120ms linear',
                        }}
                      />
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}

          <button type="submit" disabled={loading} className="submit-button">
            {loading ? 'Uploading...' : 'Submit Report'}
          </button>
        </form>

        {/* Preview modal — NOTE: stopPropagation on the overlay's own
            mousedown, otherwise the event bubbles up to the task modal's
            popup-overlay and closes BOTH dialogs at once. */}
        {isPreviewOpen && previewReport && (
          <div
            className="trm-preview-overlay"
            onMouseDown={(e) => { e.stopPropagation(); closePreview(); }}
          >
            <div className="trm-preview-panel" onMouseDown={(e) => e.stopPropagation()}>
              <div className="trm-preview-header">
                <div className="trm-preview-headings">
                  <span className="trm-preview-title">{formatType(previewReport.type)}</span>
                  <span className="trm-preview-sub">
                    {previewReport.uploaded_at
                      ? DateTime.fromISO(previewReport.uploaded_at, { zone: 'utc' })
                        .setZone('Asia/Dubai')
                        .toFormat('dd LLLL yyyy, HH:mm')
                      : ''}
                  </span>
                </div>
                <span className={`trm-preview-badge ${previewReport.is_approved ? 'trm-preview-badge--ok' : 'trm-preview-badge--pending'}`}>
                  {previewReport.is_approved ? 'Approved' : 'Waiting for approval'}
                </span>
                <button type="button" className="trm-preview-close" onClick={closePreview} title="Close preview">
                  &times;
                </button>
              </div>

              <div className="trm-preview-media">
                {isVideoUrl(previewReport.file_url) ? (
                  <video controls autoPlay>
                    <source src={previewReport.file_url} type="video/mp4" />
                  </video>
                ) : (
                  <img src={previewReport.file_url} alt="Report attachment" />
                )}
              </div>

              <div className="trm-preview-meta">
                <div className="trm-preview-meta-item">
                  <span className="trm-preview-meta-label">Task</span>
                  <span className="trm-preview-meta-value">{task?.instruction || '—'}</span>
                </div>
                <div className="trm-preview-meta-item">
                  <span className="trm-preview-meta-label">Comment</span>
                  <span className="trm-preview-meta-value">{previewReport.comment || 'No comment'}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        <LoadingPopup
          isLoading={loading || isProcessing}
          feedbackMessage={feedbackMessage}
          onClose={() => {
            // close popup message only
            setFeedbackMessage('');
          }}
        />
      </div>
    </div>
  );
}

export default TaskReportModal;
